import { IBudgetVersionService, IBudgetVersionServiceItem } from "../../../../models/budgetV2";
import { useIntl } from "react-intl";
import { formatNumberToCurrency } from "../../../../utils/stringUtils";
import { CoverageBadge } from '../../components/CoverageBadge';
import { findInitialServiceItem } from '../../components/budgetServiceForm/utils/findInitialServiceItem';

interface IServiceProps {
    service: IBudgetVersionService;
    serviceIndex: number;
    // the same service as proposed before this version's changes (budget.versionData.proposalServices)
    proposalService?: IBudgetVersionService;
}

export const Service = (props: IServiceProps) => {
    const intl = useIntl();

    const observation = props.service.observation;
    const hasObservation = observation && typeof observation === 'string' && observation.trim() !== '';

    const getItemNetValue = (item: IBudgetVersionServiceItem) => {
        const itemValue = item.value || 0;
        let clean = String(item.quantity ?? '0').replace(/[^\d.,]/g, '');
        clean = clean.replace(',', '.');
        const quantityParsed = Number.parseFloat(clean);
        const quantity = Number.isNaN(quantityParsed) ? 0 : quantityParsed;

        const discountStr = String(item.discount ?? '0');
        let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, '');
        cleanDiscountStr = cleanDiscountStr.replaceAll(',', '.');
        const itemDiscountPercent = Number.parseFloat(cleanDiscountStr) || 0;

        return itemValue * quantity * (1 - itemDiscountPercent / 100);
    };

    const serviceItems =
        (props.service.items?.length ? props.service.items : undefined)
        ?? props.service.serviceItems
        ?? [];

    const getItems = (service?: IBudgetVersionService) =>
        (service?.items?.length ? service.items : service?.serviceItems) ?? [];

    // Rows to show: the items of this version (with the proposed values to compare) plus the proposed
    // items that are no longer here, shown as removed.
    const proposedItems = getItems(props.proposalService);
    const rows = serviceItems.map((item) => ({ item, original: findInitialServiceItem(proposedItems, item), removedInThisVersion: false }));
    proposedItems.forEach((proposed, proposedIndex) => {
        if (findInitialServiceItem(serviceItems, proposed)) return;
        rows.splice(Math.min(proposedIndex, rows.length), 0, { item: { ...proposed, removed: true }, original: undefined, removedInThisVersion: true });
    });

    const previousLabel = (value: string) => (
        <div className="text-size-12 font-bold text-gray-500 whitespace-nowrap">
            {intl.formatMessage({ id: 'intl-msg:budgets.previous' })} ({value})
        </div>
    );

    const calculatedSubtotal = () => {
        return serviceItems.reduce((sum, item) => {
            if (item.isCovered === true || item.removed === true) return sum;
            return sum + getItemNetValue(item);
        }, 0);
    };

    const calculatedTruckerPayPlanSubtotal = () => {
        return serviceItems.reduce((sum, item) => {
            if (item.isCovered !== true || item.removed === true) return sum;
            return sum + getItemNetValue(item);
        }, 0);
    };

    return (
        <div className={`border border-gray-200 rounded-md my-6`}>
            <div className={`${props.serviceIndex % 2 === 0 ? 'bg-blue-50' : 'bg-gray-50'} p-3 rounded-md mb-4 flex flex-row justify-between items-center`}>
                <div className="font-bold text-size-16">{props.service.name?.trim() || intl.formatMessage({ id: 'intl-msg:budgets.service' })}</div>
            </div>

            <div className="overflow-x-auto">
                <table
                    className="table table-layout-fixed table-column-overflow-hidden table-head-filled mb-3 w-full [&_td]:border-0 [&_th]:border-0 [&_tbody_tr]:border-0 [&_tbody_tr]:border-b-0 [&_thead_tr]:border-0 [&_thead_tr]:border-b-0 [&_tbody_tr_td]:border-0 [&_tbody_tr_td]:border-b-0 [&_thead_tr_th]:border-0 [&_thead_tr_th]:border-b-0"
                    style={{ borderCollapse: 'separate', borderSpacing: 0 }}
                >
                    <thead>
                        <tr className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                            <th className="w-[7%] border-0 !border-b-0 " style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.ref' })}</span>
                            </th>
                            <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.item' })}</span>
                            </th>
                            <th className="w-[26%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.description' })}</span>
                            </th>
                            <th className="w-[10%] border-0 !border-b-0 text-center" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.quantity' })}</span>
                            </th>
                            <th className="w-[12%] border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.value' })} (R$)</span>
                            </th>
                            <th className="w-[10%] border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.discount' })}</span>
                            </th>
                            <th className="w-[13%] border-0 !border-b-0 text-center" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.coverage' })}</span>
                            </th>
                            <th className="w-[15%] border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                <span className='font-semibold text-size-12 text-gray-600'>{intl.formatMessage({ id: 'intl-msg:budgets.subtotal' })} (R$)</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(({ item, original, removedInThisVersion }, index) => {

                            const isRemoved = item.removed === true;
                            const isCovered = item.isCovered === true;
                            const notCoveredReason = item.notCoveredReason;
                            const availableIn = item.availableIn?.trim();

                            return (
                                <tr key={`${item.id ?? item.ref}-${index}`} className="border-0 !border-b-0 h-[50px]" style={{ borderBottom: 'none' }}>
                                    <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                                        {item.ref}
                                    </td>
                                    <td className="border-0 !border-b-0 relative" style={{ borderBottom: 'none' }}>
                                        {isRemoved && (
                                            <div className="text-red-400 text-size-14 font-bold px-2 absolute bottom-[-0px] left-0 pl-5 whitespace-nowrap">
                                                {intl.formatMessage({ id: removedInThisVersion ? 'intl-msg:budgets.removed' : 'intl-msg:budgets.removedByVolksTotal' })}
                                            </div>
                                        )}
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {item.item}
                                        </div>
                                    </td>
                                    <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''} truncate`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {item.description}
                                        </div>
                                    </td>
                                    <td className="border-0 !border-b-0 text-center" style={{ borderBottom: 'none' }}>
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {item.quantity}
                                        </div>
                                        {!isRemoved && original && original.quantity !== item.quantity && previousLabel(String(original.quantity ?? 0))}
                                    </td>
                                    <td className="border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {formatNumberToCurrency(item.value ?? 0)}
                                        </div>
                                        {!isRemoved && original && original.value !== item.value && previousLabel(formatNumberToCurrency(original.value ?? 0))}
                                    </td>
                                    <td className="border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {item.discount}
                                        </div>
                                    </td>
                                    <td className="border-0 !border-b-0 max-w-0 w-[13%] overflow-hidden text-center" style={{ borderBottom: 'none' }}>
                                        <CoverageBadge isCovered={isCovered} notCoveredReason={notCoveredReason} availableIn={availableIn} />
                                    </td>
                                    <td className="border-0 !border-b-0 text-right" style={{ borderBottom: 'none' }}>
                                        <div
                                            className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                                            style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                                        >
                                            {formatNumberToCurrency(getItemNetValue(item))}
                                        </div>
                                    </td>
                                </tr>
                            )
                        }
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="w-full border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                            <td className="border-0 !border-b-0 text-right" colSpan={8} style={{ borderBottom: 'none' }}>
                                <span className="text-gray-600 mr-2 text-size-12">{intl.formatMessage({ id: 'intl-msg:budgets.serviceSubtotal' })}:</span>
                                <span className="text-gray-600 pr-2 text-size-12">{formatNumberToCurrency(calculatedSubtotal())}</span>
                                {
                                    calculatedTruckerPayPlanSubtotal() > 0 ? (
                                        <div>
                                            <span className="text-[#5CB85C] mr-2 text-size-12">{intl.formatMessage({ id: 'intl-msg:budgets.truckerPayPlanValue' })}:</span>
                                            <span className="text-[#5CB85C] pr-2 text-size-12">{formatNumberToCurrency(calculatedTruckerPayPlanSubtotal())}</span>
                                        </div>
                                    ) : null
                                }
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {hasObservation && observation && (
                <div className="m-4">
                    <div className="font-bold mb-2 text-size-12 text-gray-500">{intl.formatMessage({ id: 'intl-msg:budgets.observation' })}</div>
                    <div className="text-size-14 bg-white p-3 rounded border border-gray-200">
                        {observation}
                    </div>
                </div>
            )}
        </div>
    );
}